import React, { useState, useEffect } from 'react';
import {
  User, Building, Briefcase, Clock, AlertCircle,
  Quote, ArrowRight, CheckCircle, Globe, IndianRupee
} from 'lucide-react';
import { counselorRegistrationApi, counselingApi } from '../../services/counselingApi';
import CounselorCheckoutPage from './CounselorCheckoutPage';
import logo from '../../img/image.png';

// ─── localStorage key ─────────────────────────────────────────────────────────
const FORM_STORAGE_KEY = 'counselor_reg_form_draft';

const EMPTY_FORM = {
  contact_person:    '',
  organization_name: '',
  email:             '',
  mobile:            '',
  bio:               '',
  experience_years:  '',
  expertise:         [],
  category:          '',
  languages:         [],
  gender:            '',
  mode:              '',
  availability_type: '',
  consultation_fee:  '',
  location:          '',
  website:           '',
};

// ─────────────────────────────────────────────────────────────────────────────

const CounselorRegistrationForm = () => {
  const [step,               setStep]               = useState(1);
  const [loading,            setLoading]            = useState(true);
  const [pricing,            setPricing]            = useState(null);
  const [quoteIndex,         setQuoteIndex]         = useState(0);
  const [fadeQuote,          setFadeQuote]          = useState(true);
  const [formData,           setFormData]           = useState(EMPTY_FORM);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  // ── constants ────────────────────────────────────────────────────────────
  const categoryOptions = [
    'Education Counsellor', 'Guidance Counsellor', 'Study Abroad Counsellor',
    'Admission Counsellor', 'Student Career Counsellor', 'Mental Health Counsellor',
    'Child Psychology Counsellor', 'Relationship Counsellor', 'Career Counsellor',
  ];
  const expertiseOptions = [
    'Personal / Social', 'Career / Stream', 'Clinical',
    'Workplace / Professional', 'Critical', 'Others',
    'College Admissions', 'Competitive Exams',
  ];
  const languageOptions    = ['English', 'Hindi', 'Marathi', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Gujarati'];
  const modeOptions        = ['Online', 'Offline', 'Both'];
  const genderOptions      = ['Male', 'Female', 'Other'];
  const availabilityOptions = ['Weekday', 'Weekend', 'Flexible'];

  const quotes = [
    { text: "Guidance is not about creating a path for the student, but about lighting the lamp so they can see their own way.", author: "Vidyazon Vision" },
    { text: "The influence of a good counselor can never be erased. Join us in shaping the future of education.",              author: "Education Matters" },
    { text: "Empower the next generation. Your expertise can turn a student's confusion into clarity and confidence.",          author: "Future Leaders"    },
  ];

  // ── effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadInitialData();

    const interval = setInterval(() => {
      setFadeQuote(false);
      setTimeout(() => {
        setQuoteIndex((prev) => (prev + 1) % quotes.length);
        setFadeQuote(true);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── auto-save form to localStorage on every change ────────────────────────
  useEffect(() => {
    if (loading) return; // don't overwrite with empty before data loads
    try {
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch { /* ignore */ }
  }, [formData, loading]);

  // ── data loading ──────────────────────────────────────────────────────────
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 1. Pricing
      const pricingRes = await counselorRegistrationApi.getPricing();
      setPricing(pricingRes.pricing);

      // 2. LocalStorage draft (fastest, no network)
      let savedDraft = null;
      try {
        const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
        if (raw) savedDraft = JSON.parse(raw);
      } catch { /* ignore */ }

      // 3. Base user info from localStorage
      let baseUserData = {};
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        baseUserData = {
          contact_person: u.name || u.full_name || '',
          email:          u.email || '',
          mobile:         u.mobile || u.phone || '',
        };
      } catch { /* ignore */ }

      // 4. Try DB profile
      try {
        const profileRes = await counselingApi.getCounselorProfile();
        const profile    = profileRes?.data?.counselor || profileRes?.counselor || profileRes?.data || profileRes;

        if (profile.registration_status === "pending_payment") {

          setFormData({
              contact_person: profile.contact_person || "",
              organization_name: profile.organization_name || "",
              email: profile.email || "",
              mobile: profile.mobile || "",
              bio: profile.bio || "",
              experience_years: profile.experience_years ?? "",
              expertise: profile.expertise || [],
              category: profile.category || "",
              languages: profile.languages || [],
              gender: profile.gender || "",
              mode: profile.mode || "",
              availability_type: profile.availability_type || "",
              consultation_fee: profile.consultation_fee || "",
              location: profile.location || "",
              website: profile.website || "",
          });
      
          setStep(2);
          return;
      }
      
      if (["pending_approval","approved"].includes(profile.registration_status)) {
          setRegistrationStatus(profile.registration_status);
          setStep(3);
          return;
      }
      } catch (err) {
        // 404 = no profile yet; fall through to draft/local
        console.log('ℹ️ No DB profile found — using draft/local data');
      }

      // 5. Fallback: draft → local user data
      if (savedDraft && savedDraft.email) {
        console.log('✅ Restoring from sessionStorage draft');
        setFormData({ ...EMPTY_FORM, ...savedDraft });
      } else {
        setFormData({ ...EMPTY_FORM, ...baseUserData });
      }

    } catch (err) {
      console.error('Failed to load initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── form helpers ──────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayToggle = (field, item) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter((e) => e !== item)
        : [...prev[field], item],
    }));
  };

  const validateForm = () => {
    const required = ['contact_person', 'email', 'mobile', 'experience_years', 'category', 'gender', 'mode', 'consultation_fee'];
    const missing  = required.filter((f) => !formData[f]);
    if (missing.length > 0) { alert(`Please fill in: ${missing.join(', ')}`); return false; }
    if (!formData.expertise.length || !formData.languages.length) {
      alert('Please select at least one Expertise and one Language.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const token = localStorage.getItem('token');
    if (!token) { window.location.href = '/login?type=partner'; return; }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        experience_years: Number(formData.experience_years),
        consultation_fee: Number(formData.consultation_fee),
      };

      await counselorRegistrationApi.saveDraft(payload);

      await counselingApi.updateCounselorProfile({
        registration_status: 'pending_payment',
        pricing: { 'Per Hour': Number(formData.consultation_fee) },
      });

      setStep(2);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save draft. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── render: loading spinner ───────────────────────────────────────────────
  if (loading && step === 1) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4" />
        <p className="text-gray-600 animate-pulse">Loading your profile…</p>
      </div>
    );
  }

  // ── render: success ───────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Your profile is currently <strong>{registrationStatus || 'submitted'}</strong>. You can access your dashboard to track progress.
          </p>
          <button
            onClick={() => { window.location.href = '/counselor/dashboard'; }}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── render: checkout ──────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="min-h-screen pt-24 bg-gray-50 pb-12">
        <CounselorCheckoutPage
          formData={formData}
          setStep={setStep}
          setRegistrationStatus={setRegistrationStatus}
        />
      </div>
    );
  }

  // ── render: registration form ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

          {/* LEFT: branding (sticky) */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
              <div className="flex justify-center mb-4">
                <img src={logo} alt="Vidyazon" className="h-20 w-auto object-contain drop-shadow-sm" />
              </div>
              <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
                Partner with{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Vidyazon
                </span>
              </h1>
              <p className="text-gray-600">Join India's fastest-growing counseling network.</p>
            </div>

            <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white opacity-10 rounded-full blur-xl" />
              <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-32 h-32 bg-white opacity-10 rounded-full blur-xl" />
              <Quote className="w-10 h-10 text-white/30 mb-4" />
              <div className={`transition-opacity duration-500 ease-in-out min-h-[100px] flex flex-col justify-center ${fadeQuote ? 'opacity-100' : 'opacity-0'}`}>
                <p className="text-lg font-medium leading-relaxed italic mb-4">"{quotes[quoteIndex].text}"</p>
                <p className="text-sm font-bold tracking-wider uppercase opacity-90">{quotes[quoteIndex].author}</p>
              </div>
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="p-1 h-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600" />

              <div className="p-6 md:p-8 space-y-8">

                {/* 1. Personal Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                    <User className="w-5 h-5 text-blue-600" /> Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name *</label>
                      <input name="contact_person" value={formData.contact_person} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        placeholder="e.g. Dr. Aditi Sharma" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Gender *</label>
                      <select name="gender" value={formData.gender} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white">
                        <option value="">Select Gender</option>
                        {genderOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} disabled
                        className="w-full p-3 border border-gray-300 bg-gray-100 text-gray-500 rounded-lg outline-none cursor-not-allowed"
                        title="Email cannot be changed" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mobile *</label>
                      <input type="tel" name="mobile" value={formData.mobile} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
                    </div>
                  </div>
                </div>

                {/* 2. Professional Profile */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                    <Briefcase className="w-5 h-5 text-purple-600" /> Professional Profile
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category *</label>
                      <select name="category" value={formData.category} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white">
                        <option value="">Select Category</option>
                        {categoryOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Experience (Years) *</label>
                      <input type="number" name="experience_years" value={formData.experience_years} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" min="0" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Areas of Expertise *</label>
                    <div className="flex flex-wrap gap-2">
                      {expertiseOptions.map((item) => (
                        <button key={item} onClick={() => handleArrayToggle('expertise', item)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            formData.expertise.includes(item)
                              ? 'bg-purple-600 text-white border-purple-600 shadow-md scale-105'
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-purple-300'
                          }`}>
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Languages *</label>
                    <div className="flex flex-wrap gap-2">
                      {languageOptions.map((item) => (
                        <button key={item} onClick={() => handleArrayToggle('languages', item)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            formData.languages.includes(item)
                              ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105'
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-blue-300'
                          }`}>
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 3. Availability & Fees */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                    <Clock className="w-5 h-5 text-pink-600" /> Availability & Fees
                  </h3>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Organization / Clinic</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                      <input name="organization_name" value={formData.organization_name} onChange={handleInputChange}
                        className="w-full pl-9 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        placeholder="e.g. Happy Minds Clinic" />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1.5 flex items-start gap-1">
                      <AlertCircle className="w-3 h-3 mt-0.5 text-blue-500 flex-shrink-0" />
                      <span>If independent, please enter your <strong>Full Name</strong>.</span>
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mode *</label>
                      <select name="mode" value={formData.mode} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white outline-none">
                        <option value="">Select Mode</option>
                        {modeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Availability *</label>
                      <select name="availability_type" value={formData.availability_type} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white outline-none">
                        <option value="">Select Type</option>
                        {availabilityOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Fee (₹) *</label>
                      <input type="number" name="consultation_fee" value={formData.consultation_fee} onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg outline-none" placeholder="500" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bio / About *</label>
                    <textarea name="bio" rows="3" value={formData.bio} onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg outline-none resize-none"
                      placeholder="Short bio…" />
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full group relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {loading ? 'Saving…' : 'Submit Profile & Proceed'}
                      {!loading && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                    </span>
                    <div className="absolute inset-0 bg-white/20 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CounselorRegistrationForm;